import { skipToken, useMutation, useSuspenseQueries } from '@tanstack/react-query'
import { useDeferredValue, useEffect } from 'react'
import { trpc } from '@/main'
import { useIsLivePane } from '@/components/custom/route-transition'
import { deriveProfiles, type ProfileRouteParams } from './use-root-search'
import type { RootSearch } from '@/routes/__root'
import { noRevalidate } from '@/lib/single-flight'

type ReportInput = Exclude<Parameters<typeof trpc.reports.getFrontendReport.queryOptions>[0], typeof skipToken>

// Just these keys: spreading all of `search` put `q` in the report key and refetched on every list-search keystroke.
const REPORT_INPUT_KEYS = [
  'first',
  'second',
  'selected',
  'type',
  'orgType',
  'teamId',
  'evaluation',
  'members',
  'name',
  // The two report pages paint disjoint sections, so each is its own response rather than one filtered twice.
  'pageType',
] as const satisfies readonly (keyof ReportInput)[]

function reportInput(profiles: { first: unknown; second: unknown }) {
  if (!profiles.first && !profiles.second) return skipToken
  const input = {} as ReportInput
  for (const key of REPORT_INPUT_KEYS) {
    const value = (profiles as Record<string, unknown>)[key]
    if (value !== undefined) (input as Record<string, unknown>)[key] = value
  }
  return input
}

/**
 * The report panes that read a pane-specific query — the board and the distribution — matched against the
 * pathname the LOADER is running for. Their query is declared on the LAYOUT above them but must be warmed
 * only here: see `docs/notes/scripts/route-prefetch-check.md` ("Check 12"), which fails a layout that warms it on
 * every pane.
 */
export const PANES_WITH_OWN_QUERY = /\/(board|distribution)\/?$/

// Only /report ('own') and /market need this; every other route derives its profiles from path params or search.
export type ReportSurface = 'own' | 'market'

// Belongs on the route's context so the loader and the component share ONE deriveProfiles result.
export function reportQueryOptions(deps: ReportSearchDeps, params: ProfileRouteParams, surface?: ReportSurface) {
  const profiles = deriveProfiles(deps, params, surface === 'own', 'search', surface === 'market')
  const input = reportInput({ ...deps, ...profiles })
  return input === skipToken ? undefined : trpc.reports.getFrontendReport.queryOptions(input)
}

/** The options `reportQueryOptions` returns when a profile IS selected — what components consume. */
export type ReportQueryOptions = NonNullable<ReturnType<typeof reportQueryOptions>>

export type ReportSearchDeps = Pick<RootSearch, (typeof REPORT_INPUT_KEYS)[number]>

// Exactly REPORT_INPUT_KEYS, so unrelated search churn (dialog ids, `q` keystrokes) does not re-run these loaders.
export function reportSearchDeps(search: ReportSearchDeps): ReportSearchDeps {
  return {
    first: search.first,
    second: search.second,
    selected: search.selected,
    type: search.type,
    orgType: search.orgType,
    teamId: search.teamId,
    evaluation: search.evaluation,
    members: search.members,
    name: search.name,
    pageType: search.pageType,
  }
}

// A view is marked once per mount of a report key; two panes painting one report share the flight.
const markingViews = new Set<string>()

/**
 * Charges the view of the report ON SCREEN (`reports.markViewed`): the read is side-effect-free, since a hover
 * preload warms it for a report nobody may open. Not `pageType`: the two pages are one view, and not from an inert
 * neighbouring pane the pager mounts beside the one being read.
 */
function useMarkViewed(input: unknown) {
  const live = useIsLivePane()
  const { mutate } = useMutation(trpc.reports.markViewed.mutationOptions(noRevalidate))
  // A view is always OF one profile FROM another (or a compare of both), so a single-profile report records nothing.
  const recordable = live && typeof input === 'object' && input !== null && 'first' in input && 'second' in input
  const key = recordable
    ? JSON.stringify(input, (field, value: unknown) => (field === 'pageType' ? undefined : value))
    : undefined
  useEffect(() => {
    if (!key || markingViews.has(key)) return
    markingViews.add(key)
    mutate(JSON.parse(key), { onSettled: () => markingViews.delete(key) })
  }, [key, mutate])
}

// useSuspenseQueries takes an ARRAY, so "no profile selected" is an empty array rather than a conditional hook call.
export function useReport(queryOptions: ReportQueryOptions | undefined) {
  const deferred = useDeferredValue(queryOptions)
  // Annotated: an inline `cond ? [x] : []` narrows to a tuple union that `useSuspenseQueries` can't read.
  const queries: ReportQueryOptions[] = deferred ? [deferred] : []
  const [result] = useSuspenseQueries({ queries })
  // Past the suspension, so a skeleton or a preload never counts as a view.
  useMarkViewed(deferred?.queryKey[1]?.input)
  // The KEY, not identity: the route mints fresh options whenever search this report does not key on changes.
  const isTransitioning = JSON.stringify(deferred?.queryKey) !== JSON.stringify(queryOptions?.queryKey)
  return {
    report: result?.data,
    isFetching: (result?.isFetching ?? false) || isTransitioning,
  }
}
